import React, { useState } from "react";
import { FormControl, MenuItem, Select } from "@mui/material";
import { Dropdown } from "react-bootstrap";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useDispatch } from "react-redux";
import { useIntl } from "react-intl";
import { assignPartialReturnCrew } from "../_redux/order/orderCrud";
import {
  fetchMyOrderDetailCard,
  fetchMyOrderTimeline,
} from "../_redux/order/orderAction";

/**
 * The loaders saved on a partial return (`loader_ids`, comma separated) as
 * user ids.
 *
 * @param {Object} partialReturn The `partial_return` row.
 * @returns {Array<number>} The saved loader user ids.
 */
const savedLoaderIds = (partialReturn) =>
  String(partialReturn?.loader_ids || "")
    .split(",")
    .map((id) => Number(id.trim()))
    .filter((id) => Number.isInteger(id) && id > 0);

/**
 * Driver and loaders for one partial return, on the order page's Partial
 * Return block. Saving it puts the partial return on that driver's job list
 * in the drivers' app, as assigning a driver to the order's return does.
 *
 * The parent keys this by the partial return and its saved crew, so the
 * selects start from what is saved and start afresh when that changes.
 *
 * @param {Object} props
 * @param {number|string} props.orderId The order the partial return belongs to.
 * @param {Object} props.partialReturn The `partial_return` row from the order details.
 * @param {Object} props.driverLoader The franchise's drivers and loaders (`data.drivers`, `data.loaders`).
 * @param {Array<Object>} [props.podStorage] The order's pods (`id`, `pod_number`), to name the pods going back.
 * @param {Function} props.onStart Called before each save, to clear the last
 *   message: the snack bar only shows a new one after it has been cleared.
 * @param {Function} props.onResult Called with ("success"|"error", message) after a save.
 * @returns {JSX.Element}
 */
export function PartialReturnCrew({
  orderId,
  partialReturn,
  driverLoader,
  podStorage,
  onStart,
  onResult,
}) {
  const intl = useIntl();
  const dispatch = useDispatch();
  const [driverId, setDriverId] = useState(partialReturn?.driver_id || "");
  const [loaderIds, setLoaderIds] = useState(() =>
    savedLoaderIds(partialReturn)
  );
  const [saving, setSaving] = useState(false);

  const drivers = driverLoader?.data?.drivers || [];
  const loaders = driverLoader?.data?.loaders || [];
  // Which partial return these controls are for: an order can have more than
  // one open, and each has its own crew.
  const podNumbers = String(partialReturn?.pod_ids || "")
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean)
    .map(
      (id) =>
        (podStorage || []).find((pod) => String(pod?.id) === id)?.pod_number
    )
    .filter(Boolean)
    .join(", ");
  const slot = [partialReturn?.start_time_slot, partialReturn?.end_time_slot]
    .filter(Boolean)
    .map((time) => String(time).slice(0, 5))
    .join(" - ");
  // Saved loaders who are no longer offered. They are listed so they can be
  // unticked; otherwise every save would send them again and be refused.
  const unavailableLoaderIds = loaderIds.filter(
    (id) => !loaders.some((item) => item?.user?.id === id)
  );
  // A saved driver who is no longer offered still has to show as selected.
  const savedDriverMissing =
    driverId !== "" &&
    !drivers.some((item) => item?.user?.id === Number(driverId));

  /**
   * Tick or untick one loader.
   *
   * @param {number} id The loader's user id.
   * @returns {void}
   */
  const toggleLoader = (id) =>
    setLoaderIds((current) =>
      current.includes(id)
        ? current.filter((loaderId) => loaderId !== id)
        : [...current, id]
    );

  /**
   * Clear the last message, save the crew, then reload the order and its
   * timeline.
   *
   * @returns {void}
   */
  const save = () => {
    setSaving(true);
    onStart();
    assignPartialReturnCrew(orderId, partialReturn?.id, {
      driver_id: driverId,
      loader_id: loaderIds,
    })
      .then((res) => {
        onResult("success", res?.data?.message || "");
        dispatch(fetchMyOrderTimeline(orderId));
        dispatch(fetchMyOrderDetailCard(orderId));
      })
      .catch((error) => {
        onResult(
          "error",
          error?.response?.data?.message || "The crew could not be saved."
        );
      })
      .finally(() => setSaving(false));
  };

  return (
    <>
      <div className="col-xl-12 mb-2">
        <strong>Driver for the partial return</strong>
        {partialReturn?.partial_returns_date
          ? ` on ${partialReturn.partial_returns_date}`
          : ""}
        {slot ? ` (${slot})` : ""}
        {podNumbers ? `, pods ${podNumbers}` : ""}
      </div>
      <div className="col-xl-3 mb-2">
        <FormControl fullWidth>
          <Select
            MenuProps={{
              anchorOrigin: { vertical: "bottom", horizontal: "left" },
              transformOrigin: { vertical: "top", horizontal: "left" },
            }}
            IconComponent={(props) => (
              <i {...props}>
                <KeyboardArrowDownIcon />
              </i>
            )}
            inputProps={{ "aria-label": "Partial return driver" }}
            displayEmpty
            name="partial_return_driver_id"
            className="form-control w-100"
            value={driverId}
            onChange={(e) => setDriverId(e.target.value)}
          >
            <MenuItem value="">{intl.formatMessage({ id: "Driver" })}</MenuItem>
            {savedDriverMissing && (
              <MenuItem value={driverId}>Assigned driver (not available)</MenuItem>
            )}
            {drivers.map((item) => (
              <MenuItem value={item?.user?.id} key={item?.user?.id}>
                {item?.user?.first_name || "-"}&nbsp;
                {item?.user?.last_name || ""}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>
      <div className="col-xl-3 mb-2 loader-dropdown">
        <Dropdown className="dropdown-inline" drop="down" alignRight>
          <Dropdown.Toggle
            id={`partial-return-loaders-${partialReturn?.id}`}
            variant="transparent"
            className="btn btn-light dropdown-toggle text-dark-75"
          >
            Loader{loaderIds.length > 0 ? ` (${loaderIds.length})` : ""}
          </Dropdown.Toggle>
          <Dropdown.Menu className="dropdown-menu dropdown-menu-sm dropdown-menu-right">
            <ul className="navi navi-hover">
              {unavailableLoaderIds.map((id) => (
                <li className="navi-item" key={`unavailable-${id}`}>
                  <span className="navi-text">
                    <label className="checkbox">
                      <input
                        type="checkbox"
                        checked
                        onChange={() => toggleLoader(id)}
                      />
                      <span className="mr-2"></span> Assigned loader (not
                      available)
                    </label>
                  </span>
                </li>
              ))}
              {loaders.map((item) => (
                <li className="navi-item" key={item?.user?.id}>
                  <span className="navi-text">
                    <label className="checkbox">
                      <input
                        type="checkbox"
                        checked={loaderIds.includes(item?.user?.id)}
                        onChange={() => toggleLoader(item?.user?.id)}
                      />
                      <span className="mr-2"></span>{" "}
                      {item?.user?.first_name || "-"}&nbsp;
                      {item?.user?.last_name || ""}
                    </label>
                  </span>
                </li>
              ))}
            </ul>
          </Dropdown.Menu>
        </Dropdown>
      </div>
      <div className="col-xl-6 mb-2">
        <button
          className="btn btn-primary font-weight-bolder font-size-sm mr-3 btn-sm"
          type="button"
          disabled={saving || driverId === ""}
          onClick={save}
        >
          {intl.formatMessage({ id: "SAVE" })}
        </button>
      </div>
    </>
  );
}
